import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { consumePendingCartHerbRemovals, normalizeCartItem, normalizeCartItemsFromStorage } from "../lib/cartItem";

const CartContext = createContext(null);
const STORAGE_KEY = "mossmatrix_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return normalizeCartItemsFromStorage(raw ? JSON.parse(raw) : []);
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const cartSanitizeNotified = useRef(false);

  useEffect(() => {
    if (cartSanitizeNotified.current) return;
    const removed = consumePendingCartHerbRemovals();
    if (removed.length) {
      cartSanitizeNotified.current = true;
      toast.info("Some herbs were removed from your basket", {
        description:
          removed.length === 1
            ? `${removed[0]} is not on our current launch blend menu. Choose from launch herbs in the builder.`
            : `${removed.slice(0, 3).join(", ")}${removed.length > 3 ? " and others" : ""} are not available for custom blends right now.`,
        duration: 6000,
      });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const normalized = normalizeCartItem({ ...item, qty: item.qty || 1 });
      const idx = prev.findIndex((p) => p.signature === normalized.signature);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          qty: next[idx].qty + (normalized.qty || 1),
        };
        return next.map((row) => normalizeCartItem(row));
      }
      return [...prev, normalized].map((row) => normalizeCartItem(row));
    });
  }, []);

  const removeItem = useCallback((signature) => {
    setItems((prev) => prev.filter((i) => i.signature !== signature));
  }, []);

  const updateQty = useCallback((signature, qty) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.signature === signature ? normalizeCartItem({ ...i, qty: Math.max(1, Math.floor(qty)) }) : normalizeCartItem(i),
        )
        .filter((i) => i.qty > 0),
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const count = items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQty,
        clearCart,
        subtotal,
        count,
        open,
        setOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
